// Ground plane, hills, roads, sidewalks, river banks, water.
import * as THREE from 'three';
import { QuadSoup, aabb, C, type BuildCtx } from './city_common';
import { HALF, SIDEWALK, CURB_H, WATER_Y, type Layout, RoadRaster } from './city_layout';

export interface GroundResult {
  meshes: THREE.Object3D[];
  raster: RoadRaster;
  roadSegments: { ax: number; az: number; bx: number; bz: number; width: number }[];
  waterRects: { minX: number; minZ: number; maxX: number; maxZ: number; height: number }[];
  water: THREE.Mesh;
  waterUniforms: { [k: string]: THREE.IUniform };
}

const RIVER_LEN = 2600;

export function buildGround(ctx: BuildCtx): GroundResult {
  const L = ctx.layout;
  const T = ctx.tex;
  const meshes: THREE.Object3D[] = [];
  const raster = new RoadRaster(L.bounds);
  const roadSegments: GroundResult['roadSegments'] = [];

  // ---- grass plane with a hole for the grid + river band ----------------------
  const B = L.bounds;
  const gx0 = L.roadX[0] - HALF, gx1 = L.roadX[L.cols] + HALF, gz0 = L.roadZ[0] - HALF, gz1 = L.roadZ[L.rows] + HALF;
  const rx0 = L.waterX0, rx1 = L.waterX1;
  const big = 3200;
  const shape = new THREE.Shape();
  shape.moveTo(-big, -big);
  shape.lineTo(big, -big);
  shape.lineTo(big, big);
  shape.lineTo(-big, big);
  shape.closePath();
  // hole: cross shape = grid rect union river band (in x,-z plane because we rotate -90 about x)
  const hole = new THREE.Path();
  const H = RIVER_LEN / 2;
  hole.moveTo(gx0, gz0);
  hole.lineTo(rx0, gz0);
  hole.lineTo(rx0, -H);
  hole.lineTo(rx1, -H);
  hole.lineTo(rx1, gz0);
  hole.lineTo(gx1, gz0);
  hole.lineTo(gx1, gz1);
  hole.lineTo(rx1, gz1);
  hole.lineTo(rx1, H);
  hole.lineTo(rx0, H);
  hole.lineTo(rx0, gz1);
  hole.lineTo(gx0, gz1);
  hole.closePath();
  shape.holes.push(hole);
  const gg = new THREE.ShapeGeometry(shape, 1);
  // shape is in (x, y) -> map to (x, z): rotate so y -> z
  gg.rotateX(Math.PI / 2);
  // rotateX(+90) maps y -> -z ; flip to keep hole coordinates = world z
  gg.scale(1, 1, -1);
  gg.computeVertexNormals();
  const guv = gg.attributes.uv as THREE.BufferAttribute;
  const gpos = gg.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < guv.count; i++) guv.setXY(i, gpos.getX(i) / 12, gpos.getZ(i) / 12);
  const grass = new THREE.Mesh(gg, new THREE.MeshStandardMaterial({ map: T.grass, roughness: 1, metalness: 0 }));
  grass.position.y = -0.02;
  grass.receiveShadow = true;
  grass.name = 'grass';
  meshes.push(grass);

  // grass strip between outer ring road and bounds is part of the plane already.

  // ---- hills (Buda side + far) ---------------------------------------------------
  const hillMat = new THREE.MeshStandardMaterial({ color: 0x4d6b3f, roughness: 1, metalness: 0, flatShading: false });
  const hills = new THREE.Group();
  const rng = ctx.rng;
  const hillDefs: [number, number, number, number, number][] = [];
  for (let i = 0; i < 9; i++) hillDefs.push([B.minX - 120 - rng() * 500, -700 + i * 170 + rng() * 80, 160 + rng() * 220, 40 + rng() * 70, 160 + rng() * 160]);
  for (let i = 0; i < 5; i++) hillDefs.push([B.minX - 100 + rng() * 900, B.minZ - 260 - rng() * 400, 200 + rng() * 260, 30 + rng() * 40, 140 + rng() * 120]);
  for (let i = 0; i < 5; i++) hillDefs.push([B.minX - 100 + rng() * 900, B.maxZ + 260 + rng() * 400, 200 + rng() * 260, 30 + rng() * 40, 140 + rng() * 120]);
  for (let i = 0; i < 4; i++) hillDefs.push([B.maxX + 300 + rng() * 500, -600 + i * 400 + rng() * 100, 220 + rng() * 200, 20 + rng() * 25, 220 + rng() * 200]);
  const hillGeo = new THREE.SphereGeometry(1, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2);
  const hillMesh = new THREE.InstancedMesh(hillGeo, hillMat, hillDefs.length);
  const m = new THREE.Matrix4();
  hillDefs.forEach((h, i) => {
    m.makeScale(h[2], h[3], h[4]);
    m.setPosition(h[0], -4, h[1]);
    hillMesh.setMatrixAt(i, m);
  });
  hillMesh.instanceMatrix.needsUpdate = true;
  hillMesh.computeBoundingSphere();
  hillMesh.name = 'hills';
  hills.add(hillMesh);
  meshes.push(hills);

  // ---- roads -----------------------------------------------------------------------
  const roadSoup = new QuadSoup('roads');
  const interSoup = new QuadSoup('intersections');
  const crossSoup = new QuadSoup('crosswalks');
  for (const s of L.segments) {
    roadSegments.push({ ax: s.ax, az: s.az, bx: s.bx, bz: s.bz, width: HALF * 2 });
    if (s.dir === 'h') {
      const x0 = s.ax + HALF, x1 = s.bx - HALF, z0 = s.az - HALF, z1 = s.az + HALF;
      raster.fill(x0, z0, x1, z1);
      // u across (z), v along x
      const len = x1 - x0;
      // u across the road (z), v along the road (x)
      roadSoup.quad(x0, 0, z1, x1, 0, z1, x1, 0, z0, x0, 0, z0, 1, 0, 1, len / 8, 0, len / 8, 0, 0);
    } else {
      const z0 = s.az + HALF, z1 = s.bz - HALF, x0 = s.ax - HALF, x1 = s.ax + HALF;
      raster.fill(x0, z0, x1, z1);
      const len = z1 - z0;
      // u across the road (x), v along the road (z)
      roadSoup.quad(x0, 0, z1, x1, 0, z1, x1, 0, z0, x0, 0, z0, 0, len / 8, 1, len / 8, 1, 0, 0, 0);
    }
  }
  for (const n of L.nodes) {
    if (!n) continue;
    const x0 = n.x - HALF, x1 = n.x + HALF, z0 = n.z - HALF, z1 = n.z + HALF;
    raster.fill(x0, z0, x1, z1);
    interSoup.floor(x0, z0, x1, z1, 0, 14);
    // crosswalks on each approach (inside the square, 3 m deep)
    const y = 0.012;
    const d = 3;
    if (n.n) crossSoup.quad(x0, y, z0 + d, x1, y, z0 + d, x1, y, z0 + 0.3, x0, y, z0 + 0.3, 0, 0, 1, 0, 1, 1, 0, 1);
    if (n.s) crossSoup.quad(x0, y, z1 - 0.3, x1, y, z1 - 0.3, x1, y, z1 - d, x0, y, z1 - d, 0, 0, 1, 0, 1, 1, 0, 1);
    if (n.w) crossSoup.quad(x0 + 0.3, y, z1, x0 + d, y, z1, x0 + d, y, z0, x0 + 0.3, y, z0, 0, 1, 0, 0, 1, 0, 1, 1);
    if (n.e) crossSoup.quad(x1 - d, y, z1, x1 - 0.3, y, z1, x1 - 0.3, y, z0, x1 - d, y, z0, 0, 1, 0, 0, 1, 0, 1, 1);
  }
  const roadMat = new THREE.MeshStandardMaterial({ map: T.road, roughness: 0.95, metalness: 0, vertexColors: true });
  const roads = new THREE.Mesh(roadSoup.build()!, roadMat);
  roads.receiveShadow = true;
  roads.name = 'roads';
  meshes.push(roads);
  const inter = new THREE.Mesh(interSoup.build()!, new THREE.MeshStandardMaterial({ map: T.asphalt, roughness: 0.95, metalness: 0, vertexColors: true }));
  inter.receiveShadow = true;
  inter.name = 'intersections';
  meshes.push(inter);
  const cross = new THREE.Mesh(
    crossSoup.build()!,
    new THREE.MeshStandardMaterial({ map: T.crosswalk, transparent: true, depthWrite: false, roughness: 0.9, metalness: 0, vertexColors: true, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }),
  );
  cross.receiveShadow = true;
  cross.renderOrder = 2;
  cross.name = 'crosswalks';
  meshes.push(cross);

  // ---- sidewalks + block slabs -----------------------------------------------------
  const walk = new QuadSoup('sidewalks');
  const slab = new QuadSoup('slabs');
  const darker = 0.82;
  for (const b of L.blocks) {
    // outer ring as 4 boxes (top + curb faces)
    walk.box(b.ox0, 0, b.oz0, b.ox1, CURB_H, b.oz0 + SIDEWALK, 4);
    walk.box(b.ox0, 0, b.oz1 - SIDEWALK, b.ox1, CURB_H, b.oz1, 4);
    walk.box(b.ox0, 0, b.oz0 + SIDEWALK, b.ox0 + SIDEWALK, CURB_H, b.oz1 - SIDEWALK, 4);
    walk.box(b.ox1 - SIDEWALK, 0, b.oz0 + SIDEWALK, b.ox1, CURB_H, b.oz1 - SIDEWALK, 4);
    // inner slab (paved courtyard tone), landmarks get their own ground treatment but a base slab is fine
    slab.floor(b.x0, b.z0, b.x1, b.z1, CURB_H, 4, darker, darker, darker * 0.98);
  }
  // promenades along the river + bridge sidewalks
  const promW = SIDEWALK;
  const pxA0 = L.roadX[L.riverCol] + HALF, pxA1 = pxA0 + promW;
  const pxB1 = L.roadX[L.riverCol + 1] - HALF, pxB0 = pxB1 - promW;
  const zTop = L.roadZ[0] - HALF, zBot = L.roadZ[L.rows] + HALF;
  walk.box(pxA0, 0, zTop, pxA1, CURB_H, zBot, 4);
  walk.box(pxB0, 0, zTop, pxB1, CURB_H, zBot, 4);
  // quay top (between promenade and parapet)
  for (const j of L.bridgeRows) {
    const Z = L.roadZ[j];
    walk.box(pxA1, 0, Z - HALF - SIDEWALK, pxB0, CURB_H, Z - HALF, 4);
    walk.box(pxA1, 0, Z + HALF, pxB0, CURB_H, Z + HALF + SIDEWALK, 4);
  }
  const walkMat = new THREE.MeshStandardMaterial({ map: T.sidewalk, roughness: 0.95, metalness: 0, vertexColors: true });
  const walkMesh = new THREE.Mesh(walk.build()!, walkMat);
  walkMesh.receiveShadow = true;
  walkMesh.name = 'sidewalks';
  meshes.push(walkMesh);
  const slabMesh = new THREE.Mesh(slab.build()!, walkMat);
  slabMesh.receiveShadow = true;
  slabMesh.name = 'slabs';
  meshes.push(slabMesh);

  // ---- river walls (parapet + quay face) ------------------------------------------
  const wallH = 1.05;
  const H2 = RIVER_LEN / 2;
  for (const [wx0, wx1] of [
    [pxA1, L.bankX0],
    [L.bankX1, pxB0],
  ]) {
    // quay face from water bed to ground
    ctx.solid.box((wx0 + wx1) / 2, -4, (zTop + zBot) / 2, wx1 - wx0, 4 + CURB_H, zBot - zTop, C.stoneDark);
    // parapet segments with gaps at bridges
    let zStart = zTop;
    const cuts = [...L.bridgeRows.map((j) => L.roadZ[j]), Infinity];
    for (const cz of cuts) {
      const zEnd = cz === Infinity ? zBot : cz - HALF - SIDEWALK;
      if (zEnd > zStart) {
        ctx.solid.box((wx0 + wx1) / 2, CURB_H, (zStart + zEnd) / 2, wx1 - wx0, wallH, zEnd - zStart, C.stoneGrey);
        // cap
        ctx.solid.box((wx0 + wx1) / 2, CURB_H + wallH, (zStart + zEnd) / 2, wx1 - wx0 + 0.2, 0.12, zEnd - zStart, C.stoneCream);
        ctx.colliders.push(aabb(wx0, zStart, wx1, zEnd, wallH + CURB_H));
      }
      if (cz === Infinity) break;
      zStart = cz + HALF + SIDEWALK;
    }
    // outside the grid: a sloped bank so the river continues into the fog
    ctx.solid.box((wx0 + wx1) / 2, -4, (zTop - H2) / 2, wx1 - wx0 + 6, 4, zTop + H2, C.stoneDark);
    ctx.solid.box((wx0 + wx1) / 2, -4, (zBot + H2) / 2, wx1 - wx0 + 6, 4, H2 - zBot, C.stoneDark);
  }
  // bounds walls beyond the river at the north/south edges (nobody walks along the far river bank)
  ctx.colliders.push(aabb(L.waterX0 - 1, zTop - 40, L.waterX1 + 1, zTop - 38, 2));
  ctx.colliders.push(aabb(L.waterX0 - 1, zBot + 38, L.waterX1 + 1, zBot + 40, 2));

  // ---- water ---------------------------------------------------------------------------
  const waterUniforms: { [k: string]: THREE.IUniform } = {
    uTime: { value: 0 },
    uNoise: { value: T.waterNoise },
    uDeep: { value: new THREE.Color(0x12333a) },
    uShallow: { value: new THREE.Color(0x2f5f60) },
    uSky: { value: new THREE.Color(0x87b6e0) },
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uSunColor: { value: new THREE.Color(1, 0.9, 0.7) },
    uNight: { value: 0 },
    ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
  };
  const waterMat = new THREE.ShaderMaterial({
    uniforms: waterUniforms,
    fog: true,
    vertexShader: /* glsl */ `
      varying vec3 vWorld;
      #include <fog_pars_vertex>
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorld = wp.xyz;
        vec4 mvPosition = viewMatrix * wp;
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime; uniform sampler2D uNoise; uniform vec3 uDeep; uniform vec3 uShallow; uniform vec3 uSky;
      uniform vec3 uSunDir; uniform vec3 uSunColor; uniform float uNight;
      varying vec3 vWorld;
      #include <fog_pars_fragment>
      void main() {
        vec2 uv = vWorld.xz * 0.035;
        vec3 n1 = texture2D(uNoise, uv + uTime * vec2(0.010, 0.022)).xyz * 2.0 - 1.0;
        vec3 n2 = texture2D(uNoise, uv * 2.3 - uTime * vec2(0.017, 0.011)).xyz * 2.0 - 1.0;
        vec3 n3 = texture2D(uNoise, uv * 5.1 + uTime * vec2(0.03, -0.02)).xyz * 2.0 - 1.0;
        vec3 n = normalize(vec3(n1.x + n2.x * 0.6 + n3.x * 0.3, 3.2, n1.y + n2.y * 0.6 + n3.y * 0.3));
        vec3 V = normalize(cameraPosition - vWorld);
        float ndv = max(dot(n, V), 0.0);
        float fres = pow(1.0 - ndv, 3.0);
        float ripple = 0.5 + 0.5 * n1.x * n2.y;
        vec3 base = mix(uDeep, uShallow, ripple * 0.5);
        vec3 col = mix(base, uSky * 0.85, 0.22 + 0.6 * fres);
        vec3 Hv = normalize(uSunDir + V);
        float spec = pow(max(dot(n, Hv), 0.0), 140.0);
        col += uSunColor * spec * 1.6 * max(uSunDir.y, 0.0);
        // night: darker, city light shimmer
        col *= mix(1.0, 0.35, uNight);
        col += vec3(1.0, 0.75, 0.45) * pow(max(n1.y * n3.x, 0.0), 4.0) * 0.25 * uNight;
        gl_FragColor = vec4(col, 1.0);
        #include <fog_fragment>
      }`,
  });
  const waterGeo = new THREE.PlaneGeometry(L.waterX1 - L.waterX0, RIVER_LEN, 1, 1);
  waterGeo.rotateX(-Math.PI / 2);
  const water = new THREE.Mesh(waterGeo, waterMat);
  water.position.set(L.riverCx, WATER_Y, 0);
  water.name = 'water';
  meshes.push(water);
  const waterRects = [{ minX: L.waterX0, minZ: -RIVER_LEN / 2, maxX: L.waterX1, maxZ: RIVER_LEN / 2, height: 0 }];

  ctx.updaters.push((dt) => {
    waterUniforms.uTime.value += dt;
  });
  ctx.raycast.push(grass, roads, inter, walkMesh, slabMesh);
  return { meshes, raster, roadSegments, waterRects, water, waterUniforms };
}

/** road segment id helper for other modules: yaw of travel along +x is PI/2 */
export function segmentHeading(dir: 'h' | 'v', positive: boolean): number {
  if (dir === 'h') return positive ? Math.PI / 2 : -Math.PI / 2;
  return positive ? 0 : Math.PI;
}

export { RoadRaster, type Layout };
